import { InfluenceCreator } from "@/components/feature/influence/InfluenceSection";
import { Work } from "@/components/feature/influence/WorksSection";
import creatorsData from "@/data/influence/creators.json";
import relationshipsData from "@/data/influence/relationships.json";
import worksData from "@/data/influence/works.json";
import authorshipsData from "@/data/influence/authorships.json";

// JSONデータの型定義
type CreatorData = {
  id: string;
  name: string;
  category: string;
  avatar: string;
  profile?: string;
};

type WorkData = {
  id: string;
  name: string;
  thumbnail: string;
  year: string;
  category?: string;
};

type Relationship = {
  receiveCreatorId: string;
  receiveWorkId: string;
  giveCreatorId: string;
  giveWorkId: string;
  source?: string;
  sourceUrl?: string;
  episode?: string;
};

type Authorship = {
  creatorId: string;
  workId: string;
  role: string;
};

// マスタデータのMap化（検索高速化）
const creatorsMap = new Map<string, CreatorData>(
  (creatorsData as CreatorData[]).map((c) => [c.id, c]),
);
const worksMap = new Map<string, WorkData>(
  (worksData as WorkData[]).map((w) => [w.id, w]),
);
const relationships = relationshipsData as Relationship[];
const authorships = authorshipsData as Authorship[];

// creatorId から作品一覧を authorship 経由で取得（同一作品の重複を除去）
function getWorksByCreator(creatorId: string): WorkData[] {
  const seen = new Set<string>();
  return authorships
    .filter((a) => a.creatorId === creatorId)
    .flatMap((a) => {
      if (seen.has(a.workId)) return [];
      seen.add(a.workId);
      const w = worksMap.get(a.workId);
      return w ? [w] : [];
    });
}

// workId から作家名一覧を authorship 経由で取得（複数監督に対応）
function getCreatorNamesByWork(workId: string): string[] {
  return authorships
    .filter((a) => a.workId === workId)
    .map((a) => creatorsMap.get(a.creatorId)?.name)
    .filter((name): name is string => name !== undefined);
}

// workId から { name, role } 一覧を authorship 経由で取得
function getCreatorsByWork(workId: string): { name: string; role: string }[] {
  return authorships
    .filter((a) => a.workId === workId)
    .flatMap((a) => {
      const creator = creatorsMap.get(a.creatorId);
      if (!creator) return [];
      return [{ name: creator.name, role: a.role }];
    });
}

// クリエイターIDからUI向けInfluenceCreatorに変換
// UIとの互換維持のため role フィールドに category の値を渡す
function toInfluenceCreator(creatorId: string, rel?: Relationship): InfluenceCreator | null {
  const c = creatorsMap.get(creatorId);
  if (!c) return null;
  return {
    id: c.id,
    avatar: c.avatar,
    name: c.name,
    role: c.category,
    source: rel?.source,
    sourceUrl: rel?.sourceUrl,
    episode: rel?.episode,
  };
}

// 作品IDからUI向けWorkに変換
// UIとの互換維持のため title フィールドに name の値を渡す
function toWork(workId: string, rel?: Relationship): Work | null {
  const w = worksMap.get(workId);
  if (!w) return null;
  const creators = getCreatorsByWork(w.id);
  return {
    id: w.id,
    thumbnail: w.thumbnail,
    title: w.name,
    year: w.year,
    creators: creators.length > 0 ? creators : undefined,
    category: w.category,
    source: rel?.source,
    sourceUrl: rel?.sourceUrl,
    episode: rel?.episode,
  };
}

// クリエイターのInfluence Map用データ
export type CreatorInfluenceData = {
  creator: {
    avatar: string;
    name: string;
    role: string;
    profile?: string;
  };
  outputCreators: InfluenceCreator[];
  outputWorks: Work[];
  inputCreators: InfluenceCreator[];
  inputWorks: Work[];
};

// 作品のInfluence Map用データ
export type WorkInfluenceData = {
  work: {
    thumbnail: string;
    title: string;
    year: string;
    creators: string[];
    category?: string;
  };
  outputCreators: InfluenceCreator[];
  outputWorks: Work[];
  inputCreators: InfluenceCreator[];
  inputWorks: Work[];
};

// クリエイターIDからInfluenceMapデータを取得
export function getCreatorInfluence(
  creatorId: string,
): CreatorInfluenceData | null {
  const creator = creatorsMap.get(creatorId);
  if (!creator) return null;

  const outputCreators: InfluenceCreator[] = [];
  const outputWorks: Work[] = [];
  const inputCreators: InfluenceCreator[] = [];
  const inputWorks: Work[] = [];

  // このクリエイターの作品一覧（authorships 経由）
  const creatorWorks = getWorksByCreator(creatorId);
  for (const w of creatorWorks) {
    const work = toWork(w.id);
    if (work) outputWorks.push(work);
  }

  for (const rel of relationships) {
    // このクリエイターが影響を受けた側（INPUT）
    // receiveCreatorId が一致 → give 側を INPUT として表示
    if (rel.receiveCreatorId === creatorId) {
      if (rel.giveCreatorId) {
        const c = toInfluenceCreator(rel.giveCreatorId, rel);
        if (c) inputCreators.push(c);
      } else if (rel.giveWorkId) {
        const w = toWork(rel.giveWorkId, rel);
        if (w) inputWorks.push(w);
      }
    }

    // このクリエイターが影響を与えた側（OUTPUT）
    // giveCreatorId が一致 → receive 側を OUTPUT として表示
    if (rel.giveCreatorId === creatorId) {
      if (rel.receiveCreatorId) {
        const c = toInfluenceCreator(rel.receiveCreatorId, rel);
        if (c) outputCreators.push(c);
      }
    }
  }

  // 発表年の古い順にソート
  outputWorks.sort((a, b) => parseInt(a.year) - parseInt(b.year));

  return {
    creator: {
      avatar: creator.avatar,
      name: creator.name,
      role: creator.category,
      profile: creator.profile,
    },
    outputCreators,
    outputWorks,
    inputCreators,
    inputWorks,
  };
}

// 作品IDからInfluenceMapデータを取得
export function getWorkInfluence(workId: string): WorkInfluenceData | null {
  const work = worksMap.get(workId);
  if (!work) return null;

  const creators = getCreatorNamesByWork(workId);

  const outputCreators: InfluenceCreator[] = [];
  const outputWorks: Work[] = [];
  const inputCreators: InfluenceCreator[] = [];
  const inputWorks: Work[] = [];

  for (const rel of relationships) {
    // この作品が影響を受けた側（INPUT）
    if (rel.receiveWorkId === workId) {
      if (rel.giveCreatorId) {
        const c = toInfluenceCreator(rel.giveCreatorId, rel);
        if (c) inputCreators.push(c);
      } else if (rel.giveWorkId) {
        const w = toWork(rel.giveWorkId, rel);
        if (w) inputWorks.push(w);
      }
    }

    // この作品が影響を与えた側（OUTPUT）
    if (rel.giveWorkId === workId) {
      if (rel.receiveCreatorId) {
        const c = toInfluenceCreator(rel.receiveCreatorId);
        if (c) outputCreators.push(c);
      } else if (rel.receiveWorkId) {
        const w = toWork(rel.receiveWorkId);
        if (w) outputWorks.push(w);
      }
    }
  }

  // 発表年の古い順にソート
  outputWorks.sort((a, b) => parseInt(a.year) - parseInt(b.year));

  return {
    work: {
      thumbnail: work.thumbnail,
      title: work.name,
      year: work.year,
      creators,
      category: work.category,
    },
    outputCreators,
    outputWorks,
    inputCreators,
    inputWorks,
  };
}

// 検索結果の型
export type SearchResult = {
  id: string;
  type: "creator" | "work";
  name: string;
  subLabel?: string;
};

// 名前で検索（部分一致）
export function searchInfluence(
  query: string,
  type: "creator" | "work",
): SearchResult[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return [];

  if (type === "creator") {
    return (creatorsData as CreatorData[])
      .filter((c) => c.name.toLowerCase().includes(normalizedQuery))
      .map((c) => ({
        id: c.id,
        type: "creator" as const,
        name: c.name,
        subLabel: c.category,
      }));
  }

  return (worksData as WorkData[])
    .filter((w) => w.name.toLowerCase().includes(normalizedQuery))
    .map((w) => {
      const creators = getCreatorNamesByWork(w.id);
      return {
        id: w.id,
        type: "work" as const,
        name: w.name,
        subLabel: creators.join(" / "),
      };
    });
}

// 全クリエイター一覧
export function getAllCreators(): SearchResult[] {
  return (creatorsData as CreatorData[]).map((c) => ({
    id: c.id,
    type: "creator" as const,
    name: c.name,
    subLabel: c.category,
  }));
}

// 全作品一覧
export function getAllWorks(): SearchResult[] {
  return (worksData as WorkData[]).map((w) => {
    const creators = getCreatorNamesByWork(w.id);
    return {
      id: w.id,
      type: "work" as const,
      name: w.name,
      subLabel: creators.join(" / "),
    };
  });
}

// 「影響を受けた作品がある」クリエイター一覧
export function getCreatorsWithInput(): CreatorData[] {
  const receiveIds = new Set(
    (relationshipsData as Relationship[])
      .filter((r) => r.receiveCreatorId)
      .map((r) => r.receiveCreatorId),
  );
  return (creatorsData as CreatorData[]).filter((c) => receiveIds.has(c.id));
}

// 指定クリエイターが「影響を受けた情報」を持っているか判定
export function hasCreatorInput(creatorId: string): boolean {
  return (relationshipsData as Relationship[]).some(
    (r) => r.receiveCreatorId === creatorId,
  );
}
